import { useEffect, useState } from 'react'
import { api, type KorrekturAngebot, type Notiz } from '../api'
import { link } from '../navigation'
import { dauer, uhrzeit } from '../tagebuch'
import { erinnerungsZeit } from '../erinnerungen'
import { terminZeit } from '../termine'
import type { Wartend } from '../warteschlange'
import { AntwortAnzeige } from './Fragen'
import { KorrekturAngebote } from './Korrektur'
import { PushHinweis } from './PushHinweis'

function statusText(n: Notiz, kiBereit: boolean): string | null {
  switch (n.status) {
    case 'TRANSKRIPTION':
    case 'AUSWERTUNG':
      if (!kiBereit) return 'Wartet, bis die KI-API eingerichtet ist'
      if (n.fehler) return `Neuer Versuch folgt (${n.fehler})`
      return n.status === 'TRANSKRIPTION' ? 'Wird transkribiert …' : 'Wird ausgewertet …'
    case 'FEHLER':
      return `Fehlgeschlagen: ${n.fehler ?? 'unbekannter Fehler'}`
    default:
      return null
  }
}

export function NotizKarte({
  notiz,
  kiBereit,
  onGeaendert,
  onGeloescht,
  onRueckfragen,
}: {
  notiz: Notiz
  kiBereit: boolean
  onGeaendert: (n: Notiz) => void
  onGeloescht: (id: number) => void
  /** Die Prüfung hat neue Rückfragen angelegt (Badge aktualisieren) */
  onRueckfragen?: () => void
}) {
  const [anhoeren, setAnhoeren] = useState(false)
  const [bearbeiten, setBearbeiten] = useState<string | null>(null)
  const [teilen, setTeilen] = useState(false)
  const [angebote, setAngebote] = useState<KorrekturAngebot[]>([])
  const [fehler, setFehler] = useState<string | null>(null)
  /** null: nicht geprüft, 'laeuft', sonst Anzahl neuer Rückfragen */
  const [pruefung, setPruefung] = useState<'laeuft' | number | null>(null)

  const aktion = async (f: () => Promise<void>) => {
    setFehler(null)
    try {
      await f()
    } catch (e) {
      setFehler((e as Error).message)
    }
  }

  const status = statusText(notiz, kiBereit)

  return (
    <article className={`karte notiz status-${notiz.status.toLowerCase()}`}>
      <header>
        <time dateTime={notiz.aufgenommenAm}>{uhrzeit(notiz)}</time>
        {notiz.dauerMs != null && <span className="leise">{dauer(notiz.dauerMs)}</span>}
        {!notiz.hatAudio && !notiz.uebernommenVon && <span className="leise">getippt</span>}
        {notiz.uebernommenVon && <span className="leise">von {notiz.uebernommenVon} übernommen</span>}
        {notiz.archiviert && <span className="leise">🗄 archiviert</span>}
      </header>

      {bearbeiten !== null ? (
        <>
          <textarea rows={5} value={bearbeiten} onChange={(e) => setBearbeiten(e.target.value)} />
          <div className="knoepfe">
            <button className="zweitrangig" onClick={() => setBearbeiten(null)}>
              Abbrechen
            </button>
            <button
              onClick={() =>
                aktion(async () => {
                  const alt = notiz.transkript ?? ''
                  onGeaendert(await api.transkriptAendern(notiz.id, bearbeiten))
                  setBearbeiten(null)
                  // Dieselbe Korrektur in anderen Notizen anbieten; scheitert das, bleibt die Änderung trotzdem gespeichert
                  setAngebote(await api.korrekturPruefen(notiz.id, alt, bearbeiten).catch(() => []))
                })
              }
            >
              Speichern
            </button>
          </div>
        </>
      ) : (
        <>
          {notiz.nachfrage && <p className="leise klein">💭 Antwort auf: {notiz.nachfrage}</p>}
          {notiz.transkript && <p className="transkript">{notiz.transkript}</p>}
        </>
      )}

      {status && <p className={notiz.status === 'FEHLER' ? 'status fehler' : 'status leise'}>{status}</p>}
      {!notiz.verwertbar && notiz.status === 'FERTIG' && (
        <p className="status hinweis-klein">Keine verwertbare Sprache erkannt. Vermutlich eine versehentliche Aufnahme.</p>
      )}
      {notiz.erinnerungen.some((e) => e.status === 'OFFEN') && <PushHinweis />}
      {(notiz.personen.length > 0 || notiz.tags.length > 0 || notiz.termine.length > 0 || notiz.erinnerungen.length > 0) && bearbeiten === null && (
        <p className="chips">
          {notiz.termine.map((t) => (
            <a key={t.id} className={`chip termin${t.status === 'VORGESCHLAGEN' ? ' vorschlag' : ''}`} href="#termine">
              📅 {terminZeit(t)} {t.titel}
            </a>
          ))}
          {notiz.erinnerungen.map((e) => (
            <a key={e.id} className={`chip termin${e.status === 'ERLEDIGT' ? ' vorschlag' : ''}`} href="#termine">
              ⏰ {erinnerungsZeit(e)} {e.text}
            </a>
          ))}
          {notiz.personen.map((p) => (
            <a key={p.id} className="chip person" href={link.person(p.id)}>
              {p.name}
            </a>
          ))}
          {notiz.tags.map((t) => (
            <a key={t} className="chip" href={link.tag(t)}>
              #{t}
            </a>
          ))}
        </p>
      )}
      {notiz.antworten.map((a) => (
        <div key={a.id} className="notiz-antwort">
          <p className="gefragt">{a.frage}</p>
          <AntwortAnzeige antwort={a.antwort} />
          <button
            className="link"
            onClick={() =>
              aktion(async () => {
                await api.antwortVerwerfen(a.id)
                onGeaendert({ ...notiz, antworten: notiz.antworten.filter((x) => x.id !== a.id) })
              })
            }
          >
            Antwort ausblenden
          </button>
        </div>
      ))}
      {notiz.geteiltMit.length > 0 && bearbeiten === null && !teilen && <p className="leise klein">💌 geteilt mit {notiz.geteiltMit.join(', ')}</p>}
      {teilen && (
        <TeilenDialog
          notiz={notiz}
          onFertig={(geteiltMit) => {
            setTeilen(false)
            if (geteiltMit) onGeaendert({ ...notiz, geteiltMit })
          }}
        />
      )}
      {angebote.length > 0 && <KorrekturAngebote angebote={angebote} onFertig={() => setAngebote([])} />}
      {pruefung === 'laeuft' && <p className="status leise">Wird geprüft …</p>}
      {pruefung === 0 && <p className="status hinweis-klein">Geprüft: keine neuen Rückfragen.</p>}
      {typeof pruefung === 'number' && pruefung > 0 && (
        <p className="status hinweis-klein">
          Geprüft: {pruefung === 1 ? 'eine Rückfrage' : `${pruefung} Rückfragen`} – <a href="#personen">bei den Personen beantworten</a>
        </p>
      )}
      {fehler && <p className="fehler">{fehler}</p>}
      {anhoeren && <audio controls autoPlay preload="none" src={api.audioUrl(notiz.id)} />}

      {bearbeiten === null && !teilen && (
        <footer>
          {notiz.hatAudio && (
            <button className="link" onClick={() => setAnhoeren(!anhoeren)}>
              {anhoeren ? 'Player schließen' : 'Anhören'}
            </button>
          )}
          {notiz.transkript !== null && (
            <button className="link" onClick={() => setBearbeiten(notiz.transkript ?? '')}>
              Bearbeiten
            </button>
          )}
          {notiz.status === 'FERTIG' && notiz.verwertbar && !notiz.uebernommenVon && (
            <button className="link" onClick={() => setTeilen(true)}>
              Teilen
            </button>
          )}
          {notiz.status === 'FERTIG' && notiz.verwertbar && !notiz.klarstellung && kiBereit && (
            <button
              className="link"
              disabled={pruefung === 'laeuft'}
              title="Mit deinen anderen Notizen und der Kartei abgleichen"
              onClick={() =>
                aktion(async () => {
                  setPruefung('laeuft')
                  try {
                    const { rueckfragen } = await api.notizPruefen(notiz.id)
                    setPruefung(rueckfragen)
                    if (rueckfragen > 0) onRueckfragen?.()
                  } catch (e) {
                    setPruefung(null)
                    throw e
                  }
                })
              }
            >
              Prüfen
            </button>
          )}
          <button className="link" onClick={() => aktion(async () => onGeaendert(await api.archivieren(notiz.id, !notiz.archiviert)))}>
            {notiz.archiviert ? 'Zurückholen' : 'Archivieren'}
          </button>
          {notiz.status === 'FEHLER' && notiz.hatAudio && (
            <button className="link" onClick={() => aktion(async () => onGeaendert(await api.wiederholen(notiz.id)))}>
              Erneut versuchen
            </button>
          )}
          <button
            className="link gefahr"
            onClick={() =>
              confirm('Diese Notiz samt Aufnahme endgültig löschen?') &&
              aktion(async () => {
                await api.notizLoeschen(notiz.id)
                onGeloescht(notiz.id)
              })
            }
          >
            Löschen
          </button>
        </footer>
      )}
    </article>
  )
}

/** Auswahl der Freunde, die die Notiz sehen duerfen. Liefert beim Speichern die neuen Namen, beim Abbrechen null. */
function TeilenDialog({ notiz, onFertig }: { notiz: Notiz; onFertig: (geteiltMit: string[] | null) => void }) {
  const [freunde, setFreunde] = useState<{ benutzerId: number; name: string }[] | null>(null)
  const [gewaehlt, setGewaehlt] = useState<Set<number>>(new Set())
  const [fehler, setFehler] = useState<string | null>(null)

  useEffect(() => {
    Promise.all([api.freunde(), api.freigaben(notiz.id)])
      .then(([f, freigaben]) => {
        setFreunde(f.freunde)
        setGewaehlt(new Set(freigaben.map((x) => x.benutzerId)))
      })
      .catch((e) => setFehler((e as Error).message))
  }, [notiz.id])

  const umschalten = (id: number) =>
    setGewaehlt((alt) => {
      const neu = new Set(alt)
      if (!neu.delete(id)) neu.add(id)
      return neu
    })

  const speichern = async () => {
    try {
      const freigaben = await api.freigabenSetzen(notiz.id, [...gewaehlt])
      onFertig(freigaben.map((f) => f.name))
    } catch (e) {
      setFehler((e as Error).message)
    }
  }

  return (
    <div className="teilen">
      <h3>Teilen mit</h3>
      {freunde?.length === 0 && (
        <p className="leise klein">
          Du hast noch keine Freunde. Unter <a href="#freunde">Konto → Freunde</a> kannst du jemanden anfragen.
        </p>
      )}
      {freunde?.map((f) => (
        <label key={f.benutzerId} className="auswahl-zeile">
          <input type="checkbox" checked={gewaehlt.has(f.benutzerId)} onChange={() => umschalten(f.benutzerId)} /> {f.name}
        </label>
      ))}
      <p className="leise klein">
        Freunde sehen Text, Aufnahme und Datum, nicht deine Personen, Tags und Termine. Nimmst du jemanden heraus, verschwindet die Notiz bei
        ihm wieder; was er schon gelesen oder gehört hat, lässt sich natürlich nicht zurückholen.
      </p>
      {fehler && <p className="fehler">{fehler}</p>}
      <div className="knoepfe">
        <button className="zweitrangig" onClick={() => onFertig(null)}>
          Abbrechen
        </button>
        <button onClick={speichern} disabled={!freunde}>
          Speichern
        </button>
      </div>
    </div>
  )
}

export function WartendKarte({ wartend, onVerwerfen }: { wartend: Wartend; onVerwerfen: () => void }) {
  const zeit = uhrzeit({ aufgenommenAm: wartend.aufgenommenAm, zeitzone: wartend.zeitzone })
  return (
    <article className="karte notiz wartend">
      <header>
        <time dateTime={wartend.aufgenommenAm}>{zeit}</time>
        <span className="leise">{dauer(wartend.dauerMs)}</span>
      </header>
      {wartend.fehler ? (
        <>
          <p className="status fehler">Vom Server abgelehnt: {wartend.fehler}</p>
          <footer>
            <button className="link gefahr" onClick={() => confirm('Diese Aufnahme verwerfen?') && onVerwerfen()}>
              Verwerfen
            </button>
          </footer>
        </>
      ) : (
        <p className="status leise">{navigator.onLine ? 'Wird hochgeladen …' : 'Offline – wird hochgeladen, sobald wieder Netz da ist.'}</p>
      )}
    </article>
  )
}
